import React, { useState, useEffect } from 'react';
import { ChevronLeft, Video, Image, Heart, MessageCircle, Bookmark, Users } from 'lucide-react';
import VideoPlayer from './VideoPlayer';
import ApiService from '../services/ApiService';

function WeeklyCalloutResponsesView({
  callout,
  allContent,
  contentManifest,
  relatives,
  currentUserUuid,
  onBack,
  onShowToast,
}) {
  const [currentVideo, setCurrentVideo] = useState(null);
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const [responses, setResponses] = useState(() => {
    // Match content to this specific weekly callout by questionUuid
    return (allContent || []).filter(c =>
      c.question?.questionType === 'weeklyCallout' &&
      c.question?.questionUuid === callout.questionUuid
    );
  });
  const [loading, setLoading] = useState(false);

  // If no responses found in allContent, fetch them from manifest + batch API
  useEffect(() => {
    if (responses.length > 0) return;

    const calloutUuids = (contentManifest || [])
      .filter(entry => {
        const scenario = entry.questionScenario;
        if (Array.isArray(scenario)) return scenario.some(s => s === 'WeeklyCallout');
        return scenario === 'WeeklyCallout';
      })
      .map(entry => entry.uuid);

    if (calloutUuids.length === 0) return;

    setLoading(true);
    ApiService.getContentBatch(calloutUuids)
      .then(data => {
        const previews = data.contentPreviewArray || [];
        const matched = previews.filter(c =>
          c.question?.questionType === 'weeklyCallout' &&
          c.question?.questionUuid === callout.questionUuid
        );
        setResponses(matched);
      })
      .catch(() => onShowToast?.('Failed to load responses', 'error'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getRelativeName = (content) => {
    const rel = relatives.find(r => r.uuid === content.userUuid);
    return rel?.name || content.userName || 'Unknown';
  };

  const getRelativeImage = (content) => {
    const rel = relatives.find(r => r.uuid === content.userUuid);
    return rel?.profile?.profilePictureUrl || rel?.profilePictureUrl || null;
  };

  const isUrlExpired = (expiryDate) => {
    if (!expiryDate) return false;
    const expiry = new Date(expiryDate);
    return Date.now() > expiry.getTime() - 10 * 60 * 1000;
  };

  const refreshUrlsIfNeeded = async (content) => {
    if (isUrlExpired(content.urlsExpiryDate)) {
      try {
        const refreshed = await ApiService.refreshContentUrls(content.uuid);
        setResponses(prev => prev.map(c =>
          c.uuid === content.uuid ? { ...c, ...refreshed } : c
        ));
        return { ...content, ...refreshed };
      } catch (error) {
        onShowToast?.('Failed to refresh content', 'error');
      }
    }
    return content;
  };

  const openContent = async (content) => {
    const idx = responses.findIndex(c => c.uuid === content.uuid);
    const refreshed = await refreshUrlsIfNeeded(content);
    setCurrentVideoIndex(idx);
    setCurrentVideo(refreshed);
  };

  const playNext = () => {
    const nextIdx = (currentVideoIndex + 1) % responses.length;
    setCurrentVideoIndex(nextIdx);
    setCurrentVideo(responses[nextIdx]);
  };

  const playPrevious = () => {
    const prevIdx = currentVideoIndex === 0 ? responses.length - 1 : currentVideoIndex - 1;
    setCurrentVideoIndex(prevIdx);
    setCurrentVideo(responses[prevIdx]);
  };

  const handleToggleLike = async (contentUuid) => {
    const content = responses.find(c => c.uuid === contentUuid);
    if (!content) return;
    const isLiked = (content.likes || []).some(l => l.userUuid === currentUserUuid);
    try {
      const result = isLiked
        ? await ApiService.removeLike(contentUuid)
        : await ApiService.toggleLike(contentUuid);
      const update = (c) => c.uuid === contentUuid
        ? { ...c, likeCount: result.likeCount, likes: result.likes }
        : c;
      setResponses(prev => prev.map(update));
      if (currentVideo?.uuid === contentUuid) {
        setCurrentVideo(prev => ({ ...prev, likeCount: result.likeCount, likes: result.likes }));
      }
    } catch (error) {
      onShowToast?.('Failed to update like', 'error');
    }
  };

  const handleToggleFavorite = async (contentUuid) => {
    const content = responses.find(c => c.uuid === contentUuid);
    if (!content) return;
    const isFav = !!content.favorite;
    try {
      await ApiService.toggleFavorite(contentUuid, isFav ? 'remove' : 'add');
      const update = (c) => c.uuid === contentUuid ? { ...c, favorite: !isFav } : c;
      setResponses(prev => prev.map(update));
      if (currentVideo?.uuid === contentUuid) {
        setCurrentVideo(prev => ({ ...prev, favorite: !isFav }));
      }
    } catch (error) {
      onShowToast?.('Failed to update bookmark', 'error');
    }
  };

  const handleAddComment = async (contentUuid, commentText) => {
    try {
      const result = await ApiService.addComment(contentUuid, commentText);
      const update = (c) => c.uuid === contentUuid
        ? { ...c, commentCount: result.commentCount, comments: [...(c.comments || []), result.comment] }
        : c;
      setResponses(prev => prev.map(update));
      if (currentVideo?.uuid === contentUuid) {
        setCurrentVideo(prev => ({
          ...prev,
          commentCount: result.commentCount,
          comments: [...(prev.comments || []), result.comment]
        }));
      }
    } catch (error) {
      onShowToast?.('Failed to add comment', 'error');
    }
  };

  const handleDeleteComment = async (contentUuid, commentUuid) => {
    try {
      const result = await ApiService.deleteComment(contentUuid, commentUuid);
      const update = (c) => c.uuid === contentUuid
        ? { ...c, commentCount: result.commentCount, comments: (c.comments || []).filter(cm => cm.uuid !== commentUuid) }
        : c;
      setResponses(prev => prev.map(update));
      if (currentVideo?.uuid === contentUuid) {
        setCurrentVideo(prev => ({
          ...prev,
          commentCount: result.commentCount,
          comments: (prev.comments || []).filter(cm => cm.uuid !== commentUuid)
        }));
      }
    } catch (error) {
      onShowToast?.('Failed to delete comment', 'error');
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      {/* Header */}
      <div className="bg-gray-800 sticky top-0 z-40 shadow-xl p-4">
        <div className="flex items-center gap-3 mb-3">
          <button onClick={onBack} className="p-2 hover:bg-gray-700 rounded-lg transition-colors">
            <ChevronLeft className="w-6 h-6" />
          </button>
          <div className="flex-1">
            <h1 className="text-lg font-bold">Weekly Callouts</h1>
            <p className="text-purple-300 text-xs">{callout.familyName}</p>
          </div>
        </div>

        {/* Callout info */}
        <div className="bg-purple-600/15 border border-purple-500/30 rounded-xl p-3">
          <p className="text-white font-semibold text-sm">{callout.summary || callout.question}</p>
          <div className="flex items-center gap-2 mt-2">
            <Users className="w-3.5 h-3.5 text-purple-400" />
            <span className="text-purple-300 text-xs">
              {(callout.answeredUserUuids || []).length} of {(callout.answeredUserUuids || []).length + (callout.waitingOnUserUuids || []).length} answered
            </span>
          </div>
        </div>
      </div>

      {/* Responses grid */}
      <div className="p-4">
        {loading ? (
          <div className="flex justify-center py-16">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-purple-400"></div>
          </div>
        ) : responses.length === 0 ? (
          <div className="text-center py-16">
            <Video className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400 text-lg">No responses yet</p>
            <p className="text-sm text-gray-500 mt-2">Videos will appear here as family members respond</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {responses.map((content) => {
              const relName = getRelativeName(content);
              const relImage = getRelativeImage(content);
              const isImage = content.type === 'image';
              const isLiked = (content.likes || []).some(l => l.userUuid === currentUserUuid);

              return (
                <div
                  key={content.uuid}
                  onClick={() => openContent(content)}
                  className="relative bg-gray-800 rounded-xl overflow-hidden cursor-pointer aspect-[3/4]"
                >
                  {/* Thumbnail */}
                  {content.previewUrl ? (
                    <img src={content.previewUrl} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gray-900">
                      {isImage ? (
                        <Image className="w-10 h-10 text-gray-600" />
                      ) : (
                        <Video className="w-10 h-10 text-gray-600" />
                      )}
                    </div>
                  )}

                  {/* Bottom gradient overlay */}
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent p-2.5 pt-8">
                    {/* Author */}
                    <div className="flex items-center gap-1.5 mb-1">
                      {relImage ? (
                        <img src={relImage} alt="" className="w-5 h-5 rounded-full object-cover" />
                      ) : (
                        <div className="w-5 h-5 rounded-full bg-gray-600 flex items-center justify-center">
                          <span className="text-[10px] font-semibold text-gray-300">
                            {relName[0]?.toUpperCase() || '?'}
                          </span>
                        </div>
                      )}
                      <span className="text-white text-xs font-medium truncate">{relName}</span>
                    </div>

                    {/* Engagement icons */}
                    <div className="flex items-center gap-3">
                      <button
                        onClick={(e) => { e.stopPropagation(); handleToggleLike(content.uuid); }}
                        className="flex items-center gap-1 transition-colors"
                      >
                        <Heart className={`w-3.5 h-3.5 ${isLiked ? 'text-red-500 fill-red-500' : 'text-gray-300'}`} />
                        {(content.likeCount || 0) > 0 && (
                          <span className="text-gray-300 text-[10px]">{content.likeCount}</span>
                        )}
                      </button>
                      <div className="flex items-center gap-1 text-gray-300">
                        <MessageCircle className="w-3.5 h-3.5" />
                        {(content.commentCount || 0) > 0 && (
                          <span className="text-[10px]">{content.commentCount}</span>
                        )}
                      </div>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleToggleFavorite(content.uuid); }}
                        className="flex items-center gap-1 transition-colors"
                      >
                        <Bookmark className={`w-3.5 h-3.5 ${content.favorite ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`} />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Video Player Overlay */}
      {currentVideo && (
        <VideoPlayer
          content={currentVideo}
          currentUserUuid={currentUserUuid}
          onNext={playNext}
          onPrevious={playPrevious}
          onClose={() => setCurrentVideo(null)}
          onToggleFavorite={() => handleToggleFavorite(currentVideo.uuid)}
          onToggleLike={() => handleToggleLike(currentVideo.uuid)}
          onAddComment={(text) => handleAddComment(currentVideo.uuid, text)}
          onDeleteComment={(commentUuid) => handleDeleteComment(currentVideo.uuid, commentUuid)}
          isFavorite={!!currentVideo.favorite}
          isLiked={(currentVideo.likes || []).some(l => l.userUuid === currentUserUuid)}
          onShowToast={onShowToast}
        />
      )}
    </div>
  );
}

export default WeeklyCalloutResponsesView;
